import React, { FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { CircularProgress, Stack } from '@mui/material';
import { StringParam } from 'serialize-query-params';
import { useQueryParams } from 'use-query-params';

import { SubFolderDtoStatus } from '@/api/generated';
import GeneralStatusDropdownSelect from '@/components/fields/GeneralStatusDropdownSelect';
import { useSubfolderStatusActions } from '@/hooks/useSubfolderStatusActions';
import typography from '@/theme/typography';

const URL_CONFIG_MAP = {
  locationSlug: StringParam
};

const SubFolderStatusSelect: FC = () => {
  const { watch, setValue } = useFormContext();

  const { organizationSlug } = useParams() as { organizationSlug: string };
  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);

  const currentStatus = watch('status');

  const setStatus = useCallback(
    (newStatus: SubFolderDtoStatus) => {
      setValue('status', newStatus);
    },
    [setValue]
  );

  const {
    change,
    isLoading: isStatusLoading,
    isFinalState,
    availableStatuses
  } = useSubfolderStatusActions({
    currentStatus,
    onChange: setStatus,
    organizationSlug,
    subfolderSlug: locationSlug!
  });

  const handleChange = useCallback(
    (newStatus: string) => {
      change(newStatus as SubFolderDtoStatus);
    },
    [change]
  );

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <GeneralStatusDropdownSelect
        name="status"
        onChange={handleChange}
        value={currentStatus}
        activeOptions={availableStatuses as string[]}
        isDisabled={isFinalState}
        sx={{
          mx: 2,
          '& .DropdownSelect-control': {
            minWidth: 122,
            minHeight: 'unset',
            '& .DropdownSelect-optionIcon .CircleIcon-root': {
              m: 0.25
            },
            '& .DropdownSelect-optionText .MuiTypography-root': {
              ...typography.body3,
              lineHeight: 'calc(4 / 3)'
            }
          },
          '& .DropdownSelect-indicator': {
            p: 0.5
          }
        }}
      />
      {isStatusLoading && <CircularProgress size={20} />}
    </Stack>
  );
};

export default SubFolderStatusSelect;
